<template>
  <section
    class="lp-card"
    :class="`lp-card--${headerTone}`"
    :aria-label="t('ariaLabel')"
  >
    <header class="lp-card__sticky">
      <h2 class="lp-card__title">{{ dash(productTitle) }}</h2>
      <div class="lp-card__actions">
        <span class="lp-card__badge">{{ contextBadge }}</span>
        <Tooltip
          v-if="canEditCard"
          placement="top"
          :hide-delay="150"
          :text="cardEditing ? t('saveCard') : t('editCard')"
        >
          <button
            type="button"
            class="lp-card__icon-btn"
            :disabled="cardSaving"
            :aria-label="cardEditing ? t('saveCard') : t('editCard')"
            @click="toggleCardEdit"
          >
            <i
              class="bi"
              :class="cardEditing ? 'bi-check2' : 'bi-pencil'"
              aria-hidden="true"
            />
          </button>
        </Tooltip>
        <button
          v-if="showRsdButton"
          type="button"
          class="lp-card__rsd"
          @click="openRsd"
        >
          {{ t('rsd.button') }}
        </button>
        <Tooltip
          placement="top"
          :hide-delay="150"
          :text="allExpanded ? t('collapseAll') : t('expandAll')"
        >
          <button
            type="button"
            class="lp-card__icon-btn"
            @click="toggleAllSections"
          >
            <i
              class="bi"
              :class="allExpanded ? 'bi-chevron-double-up' : 'bi-chevron-double-down'"
              aria-hidden="true"
            />
          </button>
        </Tooltip>
        <Tooltip
          placement="top"
          :hide-delay="150"
          :text="t('close')"
        >
          <button
            type="button"
            class="lp-card__icon-btn"
            :aria-label="t('close')"
            @click="emit('close')"
          >
            <i
              class="bi bi-x-lg"
              aria-hidden="true"
            />
          </button>
        </Tooltip>
      </div>
    </header>

    <div
      v-if="pending && !model.id"
      class="lp-card__loading"
    >
      {{ t('loading') }}
    </div>

    <div
      v-else
      class="lp-card__scroll"
    >
      <section
        v-if="showJourney || cardEditing"
        class="lp-card__section"
      >
        <button
          type="button"
          class="lp-card__section-title"
          @click="toggleSection('journey')"
        >
          {{ t('sections.journey') }}
          <i
            class="bi bi-chevron-down lp-card__chevron"
            :class="{ 'lp-card__chevron--collapsed': !sections.journey }"
            aria-hidden="true"
          />
        </button>
        <div v-show="sections.journey">
          <ProductJourneyTimeline
            :journey="model.product_journey"
            :submission-date="journeySubmission"
            :submission-actual="journeySubmissionActual"
            :approval-date="journeyApproval"
            :approval-actual="journeyApprovalActual"
            :editing="cardEditing"
          />
        </div>
      </section>

      <section
        v-if="clinicalIndicationRows.length"
        class="lp-card__section"
      >
        <button
          type="button"
          class="lp-card__section-title"
          @click="toggleSection('clinical')"
        >
          {{ t('sections.clinical') }}
          <i
            class="bi bi-chevron-down lp-card__chevron"
            :class="{ 'lp-card__chevron--collapsed': !sections.clinical }"
            aria-hidden="true"
          />
        </button>
        <table
          v-show="sections.clinical"
          class="lp-card__table lp-card__table--indications"
        >
          <thead>
            <tr>
              <th>{{ t('labels.indication') }}</th>
              <th>{{ t('labels.fullWording') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in clinicalIndicationRows"
              :key="row.key || index"
            >
              <td>
                <button
                  type="button"
                  class="lp-card__indication-link"
                  @click="openIndicationModal(row)"
                >
                  {{ row.text || '—' }}
                </button>
              </td>
              <td>
                <div
                  v-if="row.fullText"
                  class="lp-card__wording-block"
                >
                  {{ row.fullText }}
                </div>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <Teleport to="body">
        <div
          v-if="indicationModal"
          class="lp-indication-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="indicationModalTitle"
        >
          <button
            type="button"
            class="lp-indication-modal__backdrop"
            :aria-label="t('close')"
            @click="closeIndicationModal"
          />
          <div class="lp-indication-modal__dialog">
            <header class="lp-indication-modal__header">
              <h3 class="lp-indication-modal__title">{{ indicationModalTitle }}</h3>
              <button
                type="button"
                class="lp-indication-modal__close"
                :aria-label="t('close')"
                @click="closeIndicationModal"
              >
                <i
                  class="bi bi-x-lg"
                  aria-hidden="true"
                />
              </button>
            </header>
            <div class="lp-indication-modal__body">
              <ProductIndicationsFields
                :model-value="indicationModalRows"
                :variant="indicationModalKind === 'prototype' ? 'prototype' : 'indication'"
                readonly
              />
            </div>
          </div>
        </div>
      </Teleport>

      <section
        v-if="hasPrototypeContent"
        class="lp-card__section"
      >
        <div class="lp-card__section-title lp-card__section-title--row">
          <button
            type="button"
            class="lp-card__section-title-btn"
            @click="toggleSection('prototype')"
          >
            {{ t('sections.prototype') }}
          </button>
          <div
            class="lp-card__proto-toggle"
            role="group"
          >
            <button
              type="button"
              :class="{ 'is-active': protoView === 'text' }"
              @click="protoView = 'text'"
            >
              <i
                class="bi bi-list-ul"
                aria-hidden="true"
              />
              {{ t('protoText') }}
            </button>
            <button
              type="button"
              :class="{ 'is-active': protoView === 'timeline' }"
              @click="protoView = 'timeline'"
            >
              <i
                class="bi bi-bar-chart-steps"
                aria-hidden="true"
              />
              {{ t('protoTimeline') }}
            </button>
          </div>
          <button
            type="button"
            class="lp-card__section-chevron-only"
            @click="toggleSection('prototype')"
          >
            <i
              class="bi bi-chevron-down lp-card__chevron"
              :class="{ 'lp-card__chevron--collapsed': !sections.prototype }"
              aria-hidden="true"
            />
          </button>
        </div>
        <div v-show="sections.prototype">
          <table
            v-if="protoView === 'text'"
            class="lp-card__table"
          >
            <thead>
              <tr>
                <th>{{ t('labels.indication') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in prototypeTimelineRows"
                :key="row.key || index"
              >
                <td>
                  <button
                    v-if="row.text"
                    type="button"
                    class="lp-card__indication-link"
                    @click="openPrototypeModal(row)"
                  >
                    {{ row.text }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <ProtoIndicationsTimeline
            v-else
            :rows="prototypeTimelineRows"
            :editing="cardEditing"
            @update:comment="onPrototypeComment"
          />
        </div>
      </section>

      <section
        v-if="clinicalTrialRows.length || cardEditing"
        class="lp-card__section"
      >
        <button
          type="button"
          class="lp-card__section-title"
          @click="toggleSection('clinicalTrials')"
        >
          {{ t('sections.clinicalTrials') }}
          <i
            class="bi bi-chevron-down lp-card__chevron"
            :class="{ 'lp-card__chevron--collapsed': !sections.clinicalTrials }"
            aria-hidden="true"
          />
        </button>
        <div v-show="sections.clinicalTrials">
          <table class="lp-card__table">
            <thead>
              <tr>
                <th>{{ t('labels.studyCode') }}</th>
                <th>{{ t('labels.startPlanActual') }}</th>
                <th>{{ t('labels.endPlanActual') }}</th>
                <th v-if="cardEditing" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in clinicalTrialsDisplay"
                :key="row._key || row.key || row.id || index"
              >
                <td>
                  <input
                    v-if="cardEditing"
                    v-model="row.code"
                    type="text"
                    class="form-control form-control-sm"
                  />
                  <template v-else>{{ row.code || '—' }}</template>
                </td>
                <td class="lp-card__plan-actual">
                  <div
                    v-if="cardEditing"
                    class="lp-card__trial-pair"
                  >
                    <DatePicker
                      :model-value="trialActiveDate(row, 'start')"
                      @update:model-value="v => setTrialDate(row, 'start', v)"
                    />
                    <select
                      class="form-select form-select-sm"
                      :value="trialIsActual(row, 'start') ? 'actual' : 'plan'"
                      @change="e => setTrialKind(row, 'start', e.target.value === 'actual')"
                    >
                      <option value="plan">PLAN</option>
                      <option value="actual">ACTUAL</option>
                    </select>
                  </div>
                  <template v-else-if="row.startText">
                    <strong v-if="row.startIsActual">{{ row.startText }}</strong>
                    <template v-else>{{ row.startText }}</template>
                    <span
                      class="lp-card__tag"
                      :class="row.startIsActual ? 'lp-card__tag--actual' : 'lp-card__tag--plan'"
                    >{{ row.startIsActual ? 'ACTUAL' : 'PLAN' }}</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="lp-card__plan-actual">
                  <div
                    v-if="cardEditing"
                    class="lp-card__trial-pair"
                  >
                    <DatePicker
                      :model-value="trialActiveDate(row, 'end')"
                      @update:model-value="v => setTrialDate(row, 'end', v)"
                    />
                    <select
                      class="form-select form-select-sm"
                      :value="trialIsActual(row, 'end') ? 'actual' : 'plan'"
                      @change="e => setTrialKind(row, 'end', e.target.value === 'actual')"
                    >
                      <option value="plan">PLAN</option>
                      <option value="actual">ACTUAL</option>
                    </select>
                  </div>
                  <template v-else-if="row.endText">
                    <strong v-if="row.endIsActual">{{ row.endText }}</strong>
                    <template v-else>{{ row.endText }}</template>
                    <span
                      class="lp-card__tag"
                      :class="row.endIsActual ? 'lp-card__tag--actual' : 'lp-card__tag--plan'"
                    >{{ row.endIsActual ? 'ACTUAL' : 'PLAN' }}</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td v-if="cardEditing">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :title="t('removeTrial')"
                    @click="removeClinicalTrial(index)"
                  >
                    <i class="bi bi-trash" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            v-if="cardEditing"
            type="button"
            class="btn btn-sm page-header-btn page-header-btn--ghost mt-2"
            @click="addClinicalTrial"
          >
            <i class="bi bi-plus" aria-hidden="true" />
            {{ t('addTrial') }}
          </button>
        </div>
      </section>

      <section class="lp-card__section">
        <button
          type="button"
          class="lp-card__section-title"
          @click="toggleSection('marketAccess')"
        >
          {{ t('sections.marketAccess') }}
          <i
            class="bi bi-chevron-down lp-card__chevron"
            :class="{ 'lp-card__chevron--collapsed': !sections.marketAccess }"
            aria-hidden="true"
          />
        </button>
        <table
          v-show="sections.marketAccess"
          class="lp-card__table"
        >
          <thead>
            <tr>
              <th>{{ t('labels.parameter') }}</th>
              <th>{{ t('labels.status') }}</th>
              <th>{{ t('labels.planDate') }}</th>
              <th>{{ t('labels.actualDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in marketAccessRows"
              :key="row.key"
            >
              <td>{{ row.label }}</td>
              <td>
                <select
                  v-if="cardEditing"
                  v-model="row.block.value"
                  class="form-select form-select-sm"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <span
                  v-else
                  class="lp-card__status-pill"
                  :class="row.yes ? '' : 'lp-card__status-pill--no'"
                >{{ row.yes ? 'Yes' : 'No' }}</span>
              </td>
              <td>
                <DatePicker
                  v-if="cardEditing"
                  v-model="row.block.plan_date"
                />
                <template v-else>{{ row.planDate || '—' }}</template>
              </td>
              <td>
                <DatePicker
                  v-if="cardEditing"
                  v-model="row.block.actual_date"
                />
                <template v-else>
                  <strong v-if="row.actualDate">{{ row.actualDate }}</strong>
                  <span v-else>—</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="lp-card__section">
        <div class="lp-card__section-title lp-card__section-title--static">
          {{ t('sections.general') }}
        </div>
        <div class="lp-card__general">
          <div class="lp-card__grid lp-card__grid--panel lp-card__grid--even-6">
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.bu') }}</span>
              <span class="lp-card__value">{{ dash(relationText(model.bu)) }}</span>
            </div>
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.azdCode') }}</span>
              <span class="lp-card__value">{{ dash(model.azd_code) }}</span>
            </div>
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.inn') }}</span>
              <span class="lp-card__value">{{ dash(innLabel) }}</span>
            </div>
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.strength') }}</span>
              <span class="lp-card__value">{{ dash(strengthLabel) }}</span>
            </div>
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.dosageForm') }}</span>
              <span class="lp-card__value">{{ dash(dosageFormLabel) }}</span>
            </div>
            <div class="lp-card__field">
              <span class="lp-card__label">{{ t('labels.shelfLife') }}</span>
              <span class="lp-card__value">{{ dash(model.shelf_life_ru) }}</span>
            </div>
          </div>
          <div class="lp-card__field lp-card__field--access">
            <span class="lp-card__label">{{ t('labels.fullAccess') }}</span>
            <label
              class="lp-card__access"
              :class="{ 'lp-card__access--readonly': !cardEditing }"
            >
              <input
                v-model="model.full_access"
                type="checkbox"
                class="lp-card__access-check"
                :disabled="!cardEditing"
              />
              <span class="lp-card__access-caption">{{ t('labels.access') }}</span>
            </label>
          </div>
        </div>
      </section>

      <section class="lp-card__section">
        <button
          type="button"
          class="lp-card__section-title"
          @click="toggleSection('registration')"
        >
          {{ t('sections.registration') }}
          <i
            class="bi bi-chevron-down lp-card__chevron"
            :class="{ 'lp-card__chevron--collapsed': !sections.registration }"
            aria-hidden="true"
          />
        </button>
        <div
          v-show="sections.registration"
          class="lp-card__grid lp-card__grid--panel lp-card__grid--even-5"
        >
          <div class="lp-card__field">
            <span class="lp-card__label">{{ t('labels.mah') }}</span>
            <span class="lp-card__value">{{ dash(mahLabel) }}</span>
          </div>
          <div class="lp-card__field">
            <span class="lp-card__label">{{ t('labels.rcExpiry') }}</span>
            <span class="lp-card__value">{{ dash(rcExpiryLabel) }}</span>
          </div>
          <div class="lp-card__field">
            <span class="lp-card__label">{{ t('labels.rcNumber') }}</span>
            <span class="lp-card__value">{{ dash(model.rc_number_en || model.rc_number_ru) }}</span>
          </div>
          <div class="lp-card__field">
            <span class="lp-card__label">{{ t('labels.rcIssueDate') }}</span>
            <span class="lp-card__value">{{ dash(rcIssueLabel) }}</span>
          </div>
          <div class="lp-card__field">
            <span class="lp-card__label">{{ t('labels.regulatoryLead') }}</span>
            <span class="lp-card__value">{{ dash(model.contact_person) }}</span>
          </div>
        </div>
      </section>

      <section
        class="lp-card__risks"
        :class="{ 'is-editing': risksEditing }"
      >
        <div class="lp-card__risks-head">
          <span class="lp-card__risks-title">{{ t('sections.risk') }}</span>
          <Tooltip
            v-if="canEditRisks && !cardEditing"
            placement="top"
            :hide-delay="150"
            :text="risksEditing ? t('saveRisks') : t('editRisks')"
          >
            <button
              type="button"
              class="lp-card__risk-edit"
              :disabled="risksSaving || cardEditing"
              @click="toggleRisksEdit"
            >
              <i
                class="bi"
                :class="risksEditing ? 'bi-check2' : 'bi-pencil'"
                aria-hidden="true"
              />
            </button>
          </Tooltip>
        </div>
        <div
          v-for="row in riskRows"
          :key="row.key"
          class="lp-card__risk-row"
        >
          <span class="lp-card__risk-name">{{ t(row.labelKey) }}</span>
          <div
            class="lp-card__risk-dd"
            :class="{ 'is-open': openRiskKey === row.key }"
          >
            <button
              type="button"
              class="lp-card__risk-trigger"
              :class="riskLevelClass(ensureRiskEntry(row.key).level)"
              :disabled="!risksEditing"
              :aria-expanded="openRiskKey === row.key"
              @click.stop="toggleRiskDropdown(row.key)"
            >
              <span
                v-if="ensureRiskEntry(row.key).level"
                class="lp-card__risk-dot"
                aria-hidden="true"
              />
              <span class="lp-card__risk-label">
                {{ riskLevelDisplay(ensureRiskEntry(row.key).level) }}
              </span>
              <i
                class="bi bi-chevron-down lp-card__risk-arrow"
                aria-hidden="true"
              />
            </button>
            <div
              v-if="openRiskKey === row.key && risksEditing"
              class="lp-card__risk-menu"
              role="listbox"
            >
              <button
                v-for="opt in riskLevelOptions"
                :key="opt.key"
                type="button"
                class="lp-card__risk-option"
                :class="[
                  riskLevelClass(opt.key),
                  { 'is-selected': ensureRiskEntry(row.key).level === opt.key },
                ]"
                role="option"
                @click.stop="selectRiskLevel(row.key, opt.key)"
              >
                <span
                  class="lp-card__risk-dot"
                  aria-hidden="true"
                />
                {{ riskLevelLabel(opt) }}
              </button>
            </div>
          </div>
          <input
            :value="ensureRiskEntry(row.key).comment"
            type="text"
            class="lp-card__risk-comment"
            :disabled="!risksEditing"
            :placeholder="t('riskCommentPlaceholder')"
            @input="ensureRiskEntry(row.key).comment = $event.target.value || ''"
          >
        </div>
      </section>
    </div>

    <footer
      v-if="model.id"
      class="lp-card__footer"
    >
      <History
        model="Products"
        :id="Number(model.id)"
      />
    </footer>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import DatePicker from '@/components/datepicker.vue'
import History from '@/components/history/history.vue'
import ProductIndicationsFields from '@/components/ProductIndicationsFields.vue'
import Tooltip from '@/components/tooltip.vue'
import ProductJourneyTimeline from '@/views/home/ProductJourneyTimeline.vue'
import ProtoIndicationsTimeline from '@/views/home/ProtoIndicationsTimeline.vue'
import { useApi } from 'soykajs/composables/api'
import { isGlobalTeamUser } from '@/i18n/localeStorage.js'
import { formatDateValueForDisplay, formatHomeDateDisplay } from '@/lib/dateInput'
import {
  buildCardPayload,
  buildRiskPayload,
  CARD_CATEGORIES,
  dash,
  emptyClinicalTrial,
  emptyProductJourney,
  emptyRiskCategories,
  localizedPair,
  normalizeClinicalTrials,
  normalizeMarketAccess,
  normalizeProductJourney,
  normalizeRiskCategories,
  pickDosageForm,
  pickStrength,
  productDisplayName,
  relationText,
  RISK_CATEGORY_KEYS,
  isRsdVisible,
  shouldShowSmohPrototype,
  smohPrototypeShortText,
  smohStatusLabel,
} from '@/lib/productCard'
import { useConfigsStore } from '@/stores/configs'
import { useLockStore } from '@/stores/lock'
import { useMessageStack } from '@/stores/messagestack'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  id: { type: Number, required: true },
  context: { type: String, default: 'planned_maa' },
})

const emit = defineEmits(['close', 'open-rsd', 'saved'])

const { t, locale } = useI18n()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const configsStore = useConfigsStore()
const { get: cfGet } = configsStore
const { push } = useMessageStack()
const { lockKeys } = storeToRefs(useLockStore())

const preferEnglish = computed(() => locale.value === 'en')
const riskRows = RISK_CATEGORY_KEYS
const riskLevelOptions = cfGet('products.risk_level', [])
const risksEditing = ref(false)
const risksSaving = ref(false)
const openRiskKey = ref(null)
const protoView = ref('text')

const productLockQuery = computed(() => ({
  entity_name: 'products',
  id: props.id || 0,
}))
const productLockPath = computed(() =>
  props.id > 0 ? `products/${props.id}` : null
)

// useApi.refresh() → Promise<void>; ответ только в model
const { refresh: lockSet, model: lockResult } = useApi({
  url: 'lock',
  query: productLockQuery,
})
const { refresh: lockReset } = useApi({
  url: 'lock_reset',
  query: productLockQuery,
})

function productLockKey() {
  return productLockPath.value ? lockKeys.value[productLockPath.value] : null
}

function setProductLockKey(val) {
  if (!productLockPath.value) {
    return
  }
  const keys = { ...lockKeys.value }
  if (val === undefined || val === null) {
    delete keys[productLockPath.value]
  } else {
    keys[productLockPath.value] = val
  }
  lockKeys.value = keys
}

function isLockAuthorCurrentUser(lock) {
  const author = lock?.author
  if (!author || !user.value) {
    return false
  }
  if (author.id != null && user.value.id != null && String(author.id) === String(user.value.id)) {
    return true
  }
  return Boolean(
    author.username &&
      user.value.username &&
      String(author.username) === String(user.value.username)
  )
}

function adoptLock(lock) {
  const key = lock?.lock_key
  if (key) {
    setProductLockKey(key)
  }
}

async function acquireProductLock() {
  await lockSet()
  let result = lockResult.value
  if (result == null) {
    await lockSet()
    result = lockResult.value
  }
  if (result?.success === true && result?.lock) {
    adoptLock(result.lock)
    return true
  }
  // Своя же блокировка (повторный вход / другая вкладка) — забираем ключ
  if (result?.lock && isLockAuthorCurrentUser(result.lock)) {
    adoptLock(result.lock)
    return Boolean(productLockKey())
  }
  return false
}

async function releaseProductLock() {
  const key = productLockKey()
  if (!key) {
    return
  }
  try {
    await lockReset({
      headers: { 'X-Api-Lock': key },
    })
  } catch {
    /* lock мог истечь */
  }
  setProductLockKey(undefined)
}

const sections = reactive({
  journey: true,
  clinical: true,
  prototype: true,
  clinicalTrials: true,
  marketAccess: true,
  registration: true,
})

const allExpanded = computed(() =>
  Object.values(sections).every(Boolean)
)

function toggleSection(key) {
  sections[key] = !sections[key]
}

function toggleAllSections() {
  const next = !allExpanded.value
  Object.keys(sections).forEach(k => {
    sections[k] = next
  })
}

const isModelDeleted = computed(() => {
  const deleted = model.deleted
  return deleted === true || deleted === 1 || deleted === '1' || deleted === 'true' || deleted === 'True'
})

const canEditRisks = computed(() => {
  return userStore.can(['rsd_edit', 'catalog_edit', 'sa']) && Boolean(model.id) && !isModelDeleted.value
})

/**
 * Карандаш карточки на стартовой: сотрудник отдела регистрации,
 * руководитель отдела регистрации и администратор.
 */
const canEditCard = computed(() => {
  return (
    userStore.can(['regspec', 'reglead', 'sa']) &&
    Boolean(model.id) &&
    !isModelDeleted.value
  )
})

const cardEditing = ref(false)
const cardSaving = ref(false)

const model = reactive({
  id: null,
  bu: null,
  trade_name_en: '',
  trade_name_ru: '',
  inn_en: '',
  inn_ru: '',
  azd_code: '',
  shelf_life_ru: '',
  rc_number_en: '',
  rc_number_ru: '',
  rc_issue_date_en: null,
  rc_issue_date_ru: null,
  rc_expiry_date_en: null,
  rc_expiry_date_ru: null,
  mah_en: null,
  mah_ru: null,
  dosage_forms: [],
  indications: [],
  contact_person: '',
  rsd_id: null,
  card_smoh: null,
  new_maa_submission_date: null,
  initial_registration_date: null,
  risk_categories: emptyRiskCategories(),
  clinical_trials: [],
  market_access: normalizeMarketAccess(null),
  product_journey: emptyProductJourney(),
  full_access: false,
  deleted: false,
})

const { refresh, pending } = useApi({ model })

const productTitle = computed(() => productDisplayName(model, preferEnglish.value))
const innLabel = computed(() => localizedPair(model.inn_en, model.inn_ru, preferEnglish.value))
const mahLabel = computed(() => {
  const en = relationText(model.mah_en)
  const ru = relationText(model.mah_ru)
  if (en && ru && en !== ru) {
    return `${en} / ${ru}`
  }
  return en || ru || ''
})
const strengthLabel = computed(() => pickStrength(model, preferEnglish.value))
const dosageFormLabel = computed(() => pickDosageForm(model, preferEnglish.value))
const rcIssueLabel = computed(
  () =>
    formatHomeDateDisplay(model.rc_issue_date_en) ||
    formatHomeDateDisplay(model.rc_issue_date_ru) ||
    ''
)
const rcExpiryLabel = computed(
  () =>
    formatHomeDateDisplay(model.rc_expiry_date_en) ||
    formatHomeDateDisplay(model.rc_expiry_date_ru) ||
    ''
)

const smoh = computed(() => model.card_smoh || {})

const journeySubmission = computed(
  () =>
    smoh.value.submission_date ||
    smoh.value.planned_submission_date ||
    model.initial_registration_date ||
    model.new_maa_submission_date ||
    null
)

const journeySubmissionActual = computed(() =>
  Boolean(smoh.value.submission_actual_date)
)

const journeyApproval = computed(
  () =>
    rcIssueLabel.value ||
    smoh.value.planned_approval_date ||
    null
)

const journeyApprovalActual = computed(() => Boolean(rcIssueLabel.value))

const showJourney = computed(() => {
  if (journeySubmission.value || journeyApproval.value) {
    return true
  }
  const journey = model.product_journey || {}
  return ['clinical', 'market_access', 'release'].some(key => {
    const stage = journey[key]
    if (!stage || typeof stage !== 'object') return false
    return Object.values(stage).some(v => v != null && String(v).trim() !== '')
  })
})

const indicationModal = ref(null)
const indicationModalKind = ref('clinical')

const clinicalIndicationRows = computed(() => {
  return (model.indications || [])
    .map((item, index) => {
      const shortText = localizedPair(
        item?.short_text_en,
        item?.short_text_ru || item?.text,
        preferEnglish.value
      )
      const fullText = localizedPair(
        item?.full_text_en,
        item?.full_text_ru,
        preferEnglish.value
      )
      return {
        key: item.id || `clinical-${index}`,
        text: String(shortText || '').trim(),
        // Один блок на показание: переносы строк остаются внутри текста, не дробят UI.
        fullText: String(fullText || '').trim(),
        status: indicationStatusText(item.status),
        short_text_ru: item?.short_text_ru || item?.text || '',
        full_text_ru: item?.full_text_ru || '',
        short_text_en: item?.short_text_en || '',
        full_text_en: item?.full_text_en || '',
      }
    })
    .filter(row => row.text || row.status || row.full_text_ru || row.short_text_en || row.full_text_en)
})

const indicationModalTitle = computed(() =>
  indicationModalKind.value === 'prototype'
    ? t('sections.prototype')
    : t('indicationModal.title')
)

const indicationModalRows = computed(() => {
  if (!indicationModal.value) {
    return []
  }
  return [
    {
      _key: indicationModal.value.key || 'indication-modal',
      short_text_ru: indicationModal.value.short_text_ru || '',
      full_text_ru: indicationModal.value.full_text_ru || '',
      short_text_en: indicationModal.value.short_text_en || '',
      full_text_en: indicationModal.value.full_text_en || '',
    },
  ]
})

function openIndicationModal(row) {
  indicationModalKind.value = 'clinical'
  indicationModal.value = row
}

function openPrototypeModal(row) {
  indicationModalKind.value = 'prototype'
  indicationModal.value = {
    key: row?.key || 'smoh-prototype-modal',
    short_text_ru: row?.short_text_ru || '',
    full_text_ru: row?.full_text_ru || '',
    short_text_en: row?.short_text_en || '',
    full_text_en: row?.full_text_en || '',
  }
}

function closeIndicationModal() {
  indicationModal.value = null
  indicationModalKind.value = 'clinical'
}

const showPrototypeData = computed(() => shouldShowSmohPrototype(smoh.value))

function indicationStatusText(value) {
  const key = String(value?.key || value || '').toLowerCase()
  if (key === 'planned' || key === 'submitted' || key === 'approved') {
    return t(`indicationStatuses.${key}`)
  }
  return relationText(value)
}

function prototypeModalFields(source) {
  const text = value => String(value || '').trim()
  return {
    short_text_ru: text(source.prototype_short_ru),
    short_text_en: text(source.prototype_short_en),
    full_text_ru: text(source.prototype_full_ru || source.prototype_of_indications_ru),
    full_text_en: text(source.prototype_full_en || source.prototype_of_indications_en),
  }
}

const prototypeRows = computed(() => {
  if (!showPrototypeData.value) {
    return []
  }
  const source = smoh.value
  const text = smohPrototypeShortText(source, preferEnglish.value)
  if (!text) {
    return []
  }
  return [{
    key: 'smoh-prototype',
    text,
    status: smohStatusLabel(source),
    ...prototypeModalFields(source),
  }]
})

/** Блок только при заполненном тексте прототипа в подаче (даты отдельно не показывают блок). */
const hasPrototypeContent = computed(() => {
  if (!showPrototypeData.value) {
    return false
  }
  return prototypeRows.value.some(row => row.text)
})

function formatPlanActual(value) {
  if (value == null || value === '') return ''
  return formatHomeDateDisplay(value, { empty: '' })
}

/** Строки для Text/Timeline прототипа с датами submission/approval. */
const prototypeTimelineRows = computed(() => {
  const base = prototypeRows.value
  if (!base.length) return []
  const subPlan = formatPlanActual(
    smoh.value.planned_submission_date ||
      (!journeySubmissionActual.value ? journeySubmission.value : null)
  )
  const subAct = formatPlanActual(
    journeySubmissionActual.value ? smoh.value.submission_actual_date : null
  )
  const apprPlan = formatPlanActual(
    !journeyApprovalActual.value ? smoh.value.planned_approval_date : null
  )
  const apprAct = formatPlanActual(
    journeyApprovalActual.value ? rcIssueLabel.value : null
  )
  return base.map((row, index) => ({
    ...row,
    key: row.key || `proto-${index}`,
    name: row.text,
    text: row.text,
    status: row.status,
    submissionPlan: subPlan,
    submissionActual: subAct,
    approvalPlan: apprPlan || formatPlanActual(smoh.value.planned_approval_date),
    approvalActual: apprAct,
    comment: model.product_journey?.prototype_comment || '',
  }))
})

/**
 * Дата КИ для просмотра. Сначала тот же разбор, что у поля ввода (дд.мм.гггг),
 * иначе подпись стартовой. Пустая строка — даты нет.
 */
function presentTrialDate(value) {
  if (value == null || value === '') return ''
  const dotted = formatDateValueForDisplay(value)
  if (dotted) return dotted
  const home = formatHomeDateDisplay(value, { empty: '' })
  if (!home || home === '—' || home === '-') return ''
  return home
}

/** Одна дата колонки: факт, если он заполнен, иначе план. Читает и snake, и camelCase. */
function trialReadonly(item, which) {
  const { planKey, actKey } = trialDateKeys(which)
  const planAlt = which === 'start' ? 'startPlan' : 'endPlan'
  const actAlt = which === 'start' ? 'startActual' : 'endActual'
  const actualText = presentTrialDate(item?.[actKey]) || presentTrialDate(item?.[actAlt])
  if (actualText) return { text: actualText, actual: true }
  const planText = presentTrialDate(item?.[planKey]) || presentTrialDate(item?.[planAlt])
  if (planText) return { text: planText, actual: false }
  return { text: '', actual: false }
}

const clinicalTrialRows = computed(() => {
  const list = Array.isArray(model.clinical_trials) ? model.clinical_trials : []
  return list
    .map((item, index) => {
      const start = trialReadonly(item, 'start')
      const end = trialReadonly(item, 'end')
      return {
        key: item?.id || item?._key || `ct-${index}`,
        code: String(item?.code || '').trim(),
        startText: start.text,
        startIsActual: start.actual,
        endText: end.text,
        endIsActual: end.actual,
      }
    })
    .filter(row => row.code || row.startText || row.endText)
})

const clinicalTrialsDisplay = computed(() => {
  if (cardEditing.value) {
    return Array.isArray(model.clinical_trials) ? model.clinical_trials : []
  }
  return clinicalTrialRows.value
})

function trialDateKeys(which) {
  return which === 'start'
    ? { planKey: 'start_plan', actKey: 'start_actual' }
    : { planKey: 'end_plan', actKey: 'end_actual' }
}

function trialActiveDate(row, which) {
  const { planKey, actKey } = trialDateKeys(which)
  return row?.[actKey] || row?.[planKey] || null
}

function trialIsActual(row, which) {
  const { actKey } = trialDateKeys(which)
  return Boolean(row?.[actKey])
}

function setTrialDate(row, which, value) {
  const { planKey, actKey } = trialDateKeys(which)
  if (row[actKey]) {
    row[actKey] = value
    row[planKey] = null
  } else {
    row[planKey] = value
    row[actKey] = null
  }
}

function setTrialKind(row, which, actual) {
  const { planKey, actKey } = trialDateKeys(which)
  const current = row[actKey] || row[planKey] || null
  if (actual) {
    row[actKey] = current
    row[planKey] = null
  } else {
    row[planKey] = current
    row[actKey] = null
  }
}

function addClinicalTrial() {
  if (!Array.isArray(model.clinical_trials)) {
    model.clinical_trials = []
  }
  model.clinical_trials.push({
    ...emptyClinicalTrial(),
    _key: `ct-new-${Date.now()}`,
  })
}

function removeClinicalTrial(index) {
  if (!Array.isArray(model.clinical_trials)) return
  model.clinical_trials.splice(index, 1)
}

function onPrototypeComment(value) {
  if (!model.product_journey || typeof model.product_journey !== 'object') {
    model.product_journey = emptyProductJourney()
  }
  model.product_journey.prototype_comment = value
}

const marketAccessRows = computed(() => {
  const ma = model.market_access && typeof model.market_access === 'object'
    ? model.market_access
    : normalizeMarketAccess(null)
  const edl = ma.vednp || { value: 'no', plan_date: null, actual_date: null }
  const clin = ma.clinRecs || { value: 'no', plan_date: null, actual_date: null }
  return [
    {
      key: 'edl',
      maKey: 'vednp',
      label: t('market.edl'),
      yes: edl.value === 'yes',
      planDate: formatPlanActual(edl.plan_date),
      actualDate: formatPlanActual(edl.actual_date),
      block: edl,
    },
    {
      key: 'clin',
      maKey: 'clinRecs',
      label: t('market.clinRecs'),
      yes: clin.value === 'yes',
      planDate: formatPlanActual(clin.plan_date),
      actualDate: formatPlanActual(clin.actual_date),
      block: clin,
    },
  ]
})

const contextBadge = computed(() => {
  const category = CARD_CATEGORIES[props.context]
  if (!category) {
    return t('kpi.registered')
  }
  return t(category.statusLabelKey || category.kpiTitleKey || category.titleKey)
})

const headerTone = computed(() => {
  if (props.context === 'registered') return 'registered'
  if (props.context === 'post_approval') return 'registration'
  if (props.context === 'new_maa_registration') return 'planned'
  return 'planned'
})

const showRsdButton = computed(
  () =>
    !isGlobalTeamUser(user.value) &&
    userStore.can(['rsd_view', 'sa']) &&
    Boolean(model.id) &&
    isRsdVisible(model)
)

function ensureRiskEntry(key) {
  if (!model.risk_categories || typeof model.risk_categories !== 'object') {
    model.risk_categories = emptyRiskCategories()
  }
  if (!model.risk_categories[key] || typeof model.risk_categories[key] !== 'object') {
    model.risk_categories[key] = { level: '', comment: '' }
  }
  return model.risk_categories[key]
}

function riskLevelClass(level) {
  const key = String(level || '').toLowerCase()
  if (key === 'low') return 'is-low'
  if (key === 'medium') return 'is-medium'
  if (key === 'high') return 'is-high'
  return 'is-empty'
}

function riskLevelLabel(opt) {
  const key = String(opt?.key || '').toLowerCase()
  if (key === 'low' || key === 'medium' || key === 'high') {
    return t(`riskLevels.${key}`)
  }
  return opt?.value || ''
}

function riskLevelDisplay(level) {
  const key = String(level || '').toLowerCase()
  if (key === 'low' || key === 'medium' || key === 'high') {
    return t(`riskLevels.${key}`)
  }
  return t('notSelected')
}

function toggleRiskDropdown(key) {
  if (!risksEditing.value) {
    return
  }
  openRiskKey.value = openRiskKey.value === key ? null : key
}

function selectRiskLevel(key, level) {
  ensureRiskEntry(key).level = level || ''
  openRiskKey.value = null
}

function onRiskDocClick() {
  openRiskKey.value = null
}

function hydrate() {
  model.risk_categories = normalizeRiskCategories(model.risk_categories)
  model.market_access = normalizeMarketAccess(model.market_access)
  model.product_journey = normalizeProductJourney(model.product_journey)
  model.clinical_trials = normalizeClinicalTrials(model.clinical_trials)
  model.full_access = Boolean(model.full_access)
}

async function loadItem() {
  if (!Number.isFinite(props.id) || props.id <= 0) {
    return
  }
  if (risksEditing.value || cardEditing.value) {
    await releaseProductLock()
  }
  risksEditing.value = false
  cardEditing.value = false
  openRiskKey.value = null
  await refresh({
    url: `products/${props.id}`,
    method: 'get',
  })
  hydrate()
}

async function toggleCardEdit() {
  if (!canEditCard.value) {
    return
  }
  if (!cardEditing.value) {
    const locked = await acquireProductLock()
    if (!locked) {
      push({ type: 'error', message: t('risksLocked') })
      return
    }
    if (!model.clinical_trials.length) {
      addClinicalTrial()
    }
    cardEditing.value = true
    sections.journey = true
    sections.clinicalTrials = true
    sections.marketAccess = true
    if (hasPrototypeContent.value) {
      sections.prototype = true
    }
    return
  }
  cardSaving.value = true
  try {
    if (!productLockKey()) {
      const locked = await acquireProductLock()
      if (!locked) {
        push({ type: 'error', message: t('risksLocked') })
        return
      }
    }
    await refresh({
      url: `products/${model.id}`,
      method: 'put',
      data: buildCardPayload(model),
    })
    hydrate()
    cardEditing.value = false
    await releaseProductLock()
    push({ type: 'success', message: t('cardSaved') })
    // Full Access и др. поля меняют блок канбана — стартовая должна перезагрузить колонки.
    emit('saved', { id: Number(model.id), full_access: Boolean(model.full_access) })
  } catch {
    push({ type: 'error', message: t('cardSaveError') })
  } finally {
    cardSaving.value = false
  }
}

async function toggleRisksEdit() {
  if (!canEditRisks.value) {
    return
  }
  if (!risksEditing.value) {
    const locked = await acquireProductLock()
    if (!locked) {
      push({ type: 'error', message: t('risksLocked') })
      return
    }
    risksEditing.value = true
    return
  }
  openRiskKey.value = null
  risksSaving.value = true
  try {
    if (!productLockKey()) {
      const locked = await acquireProductLock()
      if (!locked) {
        push({ type: 'error', message: t('risksLocked') })
        return
      }
    }
    await refresh({
      url: `products/${model.id}`,
      method: 'put',
      data: buildRiskPayload(model.risk_categories),
    })
    hydrate()
    risksEditing.value = false
    await releaseProductLock()
    push({ type: 'success', message: t('risksSaved') })
  } catch {
    push({ type: 'error', message: t('risksSaveError') })
  } finally {
    risksSaving.value = false
  }
}

async function openRsd() {
  if (model.rsd_id) {
    emit('open-rsd', model.rsd_id)
    return
  }
  const { model: rsdModel, refresh: createRsd } = useApi({
    url: `products/${model.id}/rsd`,
    immediate: false,
  })
  await createRsd()
  if (rsdModel.value?.id) {
    model.rsd_id = rsdModel.value.id
    emit('open-rsd', rsdModel.value.id)
  }
}

watch(
  () => props.id,
  id => {
    if (id > 0) {
      loadItem()
    }
  },
  { immediate: true }
)

watch(risksEditing, editing => {
  if (!editing) {
    openRiskKey.value = null
  }
})

onMounted(async () => {
  await configsStore.fillEntity('products')
  document.addEventListener('click', onRiskDocClick)
})

onUnmounted(() => {
  document.removeEventListener('click', onRiskDocClick)
  releaseProductLock()
})
</script>

<i18n>
{
  "ru": {
    "loading": "Загрузка…",
    "notSelected": "—",
    "riskCommentPlaceholder": "Кратко опишите причину статуса риска",
    "editRisks": "Редактировать риски",
    "saveRisks": "Сохранить риски",
    "risksSaved": "Риски сохранены",
    "risksSaveError": "Не удалось сохранить риски",
    "risksLocked": "Редактирование невозможно: запись уже редактируется другим пользователем",
    "editCard": "Редактировать карточку",
    "saveCard": "Сохранить карточку",
    "cardSaved": "Карточка сохранена",
    "cardSaveError": "Не удалось сохранить карточку",
    "addTrial": "Добавить исследование",
    "removeTrial": "Удалить строку",
    "commentPlaceholder": "Комментарий",
    "close": "Закрыть",
    "ariaLabel": "Карточка продукта",
    "collapseAll": "Свернуть все",
    "expandAll": "Развернуть все",
    "protoText": "Текст",
    "protoTimeline": "Таймлайн",
    "yes": "Да",
    "no": "Нет",
    "rsd": { "button": "RSD" },
    "indicationModal": {
      "title": "Clinical indications"
    },
    "sections": {
      "journey": "Product Journey Timeline",
      "general": "General information",
      "registration": "Registration status",
      "clinical": "Clinical indications",
      "clinicalTrials": "Clinical Trials",
      "marketAccess": "Market Access",
      "prototype": "Prototype of Indications",
      "risk": "Risk categories"
    },
    "labels": {
      "bu": "Business Unit",
      "azdCode": "AZD Code",
      "inn": "INN",
      "strength": "Strength",
      "dosageForm": "Dosage form",
      "shelfLife": "Shelf life",
      "mah": "MAH",
      "rcExpiry": "RC expiry date",
      "rcNumber": "RC number",
      "rcIssueDate": "RC Issue Date",
      "regulatoryLead": "Regulatory Lead",
      "indications": "Indications",
      "indication": "Indication",
      "fullWording": "Full wording",
      "status": "Status",
      "comment": "Comment",
      "submissionPlanActual": "Submission (plan / actual)",
      "approvalPlanActual": "Approval (plan / actual)",
      "studyCode": "Study code",
      "startPlanActual": "Start (plan / actual)",
      "endPlanActual": "End (plan / actual)",
      "parameter": "Parameter",
      "planDate": "Plan date",
      "actualDate": "Actual date",
      "access": "Доступ",
      "fullAccess": "Full Access / Полный доступ"
    },
    "market": {
      "edl": "EDL",
      "clinRecs": "Clinical guidelines"
    },
    "risk": {
      "localCt": "Local CT",
      "eaeuGmpc": "EAEU GMPc",
      "shelfLife": "Shelf-life",
      "samples": "Samples",
      "tradeName": "Trade name"
    },
    "riskLevels": { "low": "Low", "medium": "Medium", "high": "High" },
    "indicationStatuses": {
      "planned": "Planned",
      "submitted": "Submitted",
      "approved": "Approved"
    },
    "kpi": {
      "plannedShort": "Planned",
      "plannedNewMaa": "Planned New MAA",
      "submittedNewMaa": "Submitted New MAA",
      "inRegistration": "Submitted New MAA",
      "variation": "Variation",
      "registered": "Variation"
    }
  },
  "en": {
    "loading": "Loading…",
    "notSelected": "—",
    "riskCommentPlaceholder": "Briefly describe the reason for the risk status",
    "editRisks": "Edit risks",
    "saveRisks": "Save risks",
    "risksSaved": "Risk categories saved",
    "risksSaveError": "Failed to save risk categories",
    "risksLocked": "Editing is not available: another user is already editing this record",
    "editCard": "Edit card",
    "saveCard": "Save card",
    "cardSaved": "Card saved",
    "cardSaveError": "Failed to save card",
    "addTrial": "Add study",
    "removeTrial": "Remove row",
    "commentPlaceholder": "Comment",
    "close": "Close",
    "ariaLabel": "Product card",
    "collapseAll": "Collapse all",
    "expandAll": "Expand all",
    "protoText": "Text",
    "protoTimeline": "Timeline",
    "yes": "Yes",
    "no": "No",
    "rsd": { "button": "RSD" },
    "indicationModal": {
      "title": "Clinical indications"
    },
    "sections": {
      "journey": "Product Journey Timeline",
      "general": "General information",
      "registration": "Registration status",
      "clinical": "Clinical indications",
      "clinicalTrials": "Clinical Trials",
      "marketAccess": "Market Access",
      "prototype": "Prototype of Indications",
      "risk": "Risk categories"
    },
    "labels": {
      "bu": "Business Unit",
      "azdCode": "AZD Code",
      "inn": "INN",
      "strength": "Strength",
      "dosageForm": "Dosage form",
      "shelfLife": "Shelf life",
      "mah": "MAH",
      "rcExpiry": "RC expiry date",
      "rcNumber": "RC number",
      "rcIssueDate": "RC Issue Date",
      "regulatoryLead": "Regulatory Lead",
      "indications": "Indications",
      "indication": "Indication",
      "fullWording": "Full wording",
      "status": "Status",
      "comment": "Comment",
      "submissionPlanActual": "Submission (plan / actual)",
      "approvalPlanActual": "Approval (plan / actual)",
      "studyCode": "Study code",
      "startPlanActual": "Start (plan / actual)",
      "endPlanActual": "End (plan / actual)",
      "parameter": "Parameter",
      "planDate": "Plan date",
      "actualDate": "Actual date",
      "access": "Access",
      "fullAccess": "Full Access"
    },
    "market": {
      "edl": "EDL",
      "clinRecs": "Clinical guidelines"
    },
    "risk": {
      "localCt": "Local CT",
      "eaeuGmpc": "EAEU GMPc",
      "shelfLife": "Shelf-life",
      "samples": "Samples",
      "tradeName": "Trade name"
    },
    "riskLevels": { "low": "Low", "medium": "Medium", "high": "High" },
    "indicationStatuses": {
      "planned": "Planned",
      "submitted": "Submitted",
      "approved": "Approved"
    },
    "kpi": {
      "plannedShort": "Planned",
      "plannedNewMaa": "Planned New MAA",
      "submittedNewMaa": "Submitted New MAA",
      "inRegistration": "Submitted New MAA",
      "variation": "Variation",
      "registered": "Variation"
    }
  }
}
</i18n>

<style scoped>
.lp-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  flex: 1 1 0;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: 100%;
}
.lp-card__sticky {
  position: relative;
  z-index: 5;
  flex: 0 0 auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 20px;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
.lp-card--planned .lp-card__sticky {
  background: linear-gradient(135deg, #830051 0%, #9e1a6b 50%, #b83d85 100%);
}
.lp-card--registration .lp-card__sticky {
  background: linear-gradient(135deg, #5e2175 0%, #7b3a95 50%, #9655b0 100%);
}
.lp-card--registered .lp-card__sticky {
  background: linear-gradient(135deg, #0d5a8a 0%, #2e7ab0 50%, #4a96c8 100%);
}
.lp-card__title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
}
.lp-card__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.lp-card__badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  white-space: nowrap;
}
.lp-card__rsd {
  background: rgba(255, 255, 255, 0.18);
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.lp-card__rsd:hover {
  background: rgba(255, 255, 255, 0.32);
}
.lp-card__icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.lp-card__scroll {
  padding: 8px 20px 12px;
  overflow: auto;
  overscroll-behavior: contain;
  flex: 1 1 0;
  min-height: 0;
}
.lp-card__footer {
  flex: 0 0 auto;
  flex-shrink: 0;
  padding: 10px 20px 16px;
  display: flex;
  justify-content: flex-start;
  border-top: 1px solid rgba(0, 51, 160, 0.08);
  background: #fff;
}
.lp-card__footer :deep(.history-link-wrapper) {
  margin-top: 0;
  margin-bottom: 0;
}
.lp-card__loading {
  padding: 2rem;
  text-align: center;
  color: #7a8e98;
}
.lp-card__section {
  margin-top: 16px;
  margin-bottom: 4px;
}
.lp-card__section-title {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  background: #eaf2ff;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  font-family: "Roboto Slab", "Georgia", serif;
  font-size: 12px;
  font-weight: 700;
  color: #001f63;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  text-align: left;
}
.lp-card__section-title--static {
  cursor: default;
}
.lp-card__section-title--row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: default;
}
.lp-card__section-title-btn {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  font-weight: inherit;
  font-family: inherit;
  cursor: pointer;
  flex: 1;
  text-align: left;
}
.lp-card__section-chevron-only {
  border: 0;
  background: transparent;
  color: #001f63;
  cursor: pointer;
  padding: 0;
}
.lp-card__chevron {
  transition: transform 0.25s ease;
  font-size: 11px;
}
.lp-card__chevron--collapsed {
  transform: rotate(-90deg);
}
.lp-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px 16px;
  margin-bottom: 8px;
}
.lp-card__grid--panel {
  padding: 12px;
  margin-top: 8px;
  background: rgba(0, 51, 160, 0.02);
  border-radius: 10px;
}
.lp-card__grid--even-6 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}
.lp-card__general {
  display: flex;
  align-items: flex-start;
  gap: 12px 16px;
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 12px;
  background: rgba(0, 51, 160, 0.02);
  border-radius: 10px;
}
.lp-card__general > .lp-card__grid {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  background: transparent;
}
.lp-card__field--access {
  flex: 0 0 auto;
  margin-left: auto;
  padding: 4px 2px;
}
.lp-card__field--access .lp-card__label {
  white-space: nowrap;
}
.lp-card__grid--even-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}
.lp-card__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.lp-card__grid--panel .lp-card__field {
  /* без белых «облачков» — общий серо-голубой фон панели */
  padding: 4px 2px !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}
.lp-card__label {
  font-size: 10px;
  font-weight: 600;
  color: #7a8e98;
  letter-spacing: 0;
  text-transform: none;
}
.lp-card__value {
  font-size: 12px;
  font-weight: 500;
  color: #071b3a;
  word-break: break-word;
}
.lp-card__value--date {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.lp-card__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-bottom: 8px;
  th,
  td {
    border: 0;
    border-bottom: 1px solid #e6ecf3;
    padding: 8px;
    text-align: left;
  }
  th {
    background: #f5f8fc;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    color: #071b3a;
  }
  td {
    color: #071b3a;
    font-weight: 500;
  }
}
.lp-card__status-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  background: #e8f5e9;
  color: #2e7d32;
}
.lp-card__status-pill--no {
  background: #ffeeee;
  color: #c62828;
}
.lp-card__plan-actual {
  font-size: 10px;
  white-space: nowrap;
}
.lp-card__tag {
  margin-left: 4px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}
.lp-card__tag--actual {
  color: #2e7d32;
}
.lp-card__tag--plan {
  color: #7a8e98;
}
.lp-card__proto-toggle {
  display: inline-flex;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(0, 51, 160, 0.18);
  button {
    border: 0;
    background: #fff;
    color: #536579;
    font-size: 10px;
    font-weight: 700;
    padding: 4px 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }
  .is-active {
    background: #001f63;
    color: #fff;
  }
}
.lp-card__risks {
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 10px 14px 12px;
  border-radius: 16px;
  background: #eaf2ff;
  border: 1px solid rgba(0, 51, 160, 0.16);
  color: #071b3a;
  position: relative;
}
.lp-card__risks-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  min-height: 0;
}
.lp-card__risks-title {
  font-size: 11px;
  font-weight: 600;
  color: #001f63;
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
  padding: 0;
}
.lp-card__risk-edit {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(0, 51, 160, 0.18);
  background: #fff;
  color: #001f63;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.lp-card__risk-edit:hover {
  background: rgba(0, 51, 160, 0.08);
  border-color: #0033a0;
}
.lp-card__risk-edit:disabled {
  opacity: 0.6;
  cursor: wait;
}
.lp-card__risk-row {
  display: grid;
  grid-template-columns: minmax(150px, 0.9fr) 120px minmax(0, 1.6fr);
  align-items: center;
  column-gap: 12px;
  row-gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 51, 160, 0.16);
}
.lp-card__risk-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.lp-card__risk-name {
  font-size: 11px;
  font-weight: 500;
  min-width: 0;
  color: #071b3a;
  line-height: 1.3;
  display: flex;
  align-items: center;
}
.lp-card__risk-dd {
  position: relative;
  width: 120px;
  max-width: 100%;
  justify-self: start;
  user-select: none;
}
.lp-card__risk-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(0, 51, 160, 0.14);
  background: #fff;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: #071b3a;
  outline: none;
}
.lp-card__risk-trigger:disabled {
  cursor: default;
  opacity: 0.92;
  pointer-events: none;
}
.lp-card__risks.is-editing .lp-card__risk-trigger:not(:disabled):hover {
  border-color: #0033a0;
  box-shadow: 0 0 0 3px rgba(0, 51, 160, 0.08);
}
.lp-card__risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
}
.lp-card__risk-label {
  flex: 1;
  text-align: left;
  min-width: 0;
}
.lp-card__risk-arrow {
  margin-left: auto;
  font-size: 10px;
  opacity: 0.5;
  transition: transform 0.2s ease;
}
.lp-card__risk-dd.is-open .lp-card__risk-arrow {
  transform: rotate(180deg);
}
.lp-card__risk-trigger.is-low {
  background: #f1f8f2;
  color: #2e7d32;
  border-color: rgba(0, 51, 160, 0.14);
}
.lp-card__risk-trigger.is-medium {
  background: #fff8f0;
  color: #e65100;
  border-color: rgba(0, 51, 160, 0.14);
}
.lp-card__risk-trigger.is-high {
  background: #fff5f5;
  color: #c62828;
  border-color: rgba(0, 51, 160, 0.14);
}
.lp-card__risk-trigger.is-empty {
  background: #fff;
  color: #536579;
  border-color: rgba(0, 51, 160, 0.14);
}
.lp-card__risk-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 130px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(0, 51, 160, 0.16);
  border-radius: 12px;
  box-shadow: 0 12px 36px rgba(0, 35, 90, 0.14);
  padding: 4px;
  z-index: 20;
}
.lp-card__risk-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}
.lp-card__risk-option:hover,
.lp-card__risk-option.is-selected {
  background: #eaf2ff;
}
.lp-card__risk-option.is-low {
  color: #2e7d32;
}
.lp-card__risk-option.is-medium {
  color: #e65100;
}
.lp-card__risk-option.is-high {
  color: #c62828;
}
.lp-card__risk-option.is-selected::after {
  content: "✓";
  margin-left: auto;
  font-size: 12px;
}
.lp-card__risk-comment {
  width: 100%;
  min-width: 0;
  padding: 7px 12px;
  border: 1px solid rgba(0, 51, 160, 0.14);
  border-radius: 999px;
  font-size: 11px;
  background: #fff;
  color: #071b3a;
  outline: none;
  box-sizing: border-box;
}
.lp-card__risk-comment:disabled {
  pointer-events: none;
  background: rgba(255, 255, 255, 0.65);
  border-color: rgba(0, 51, 160, 0.14);
}
.lp-card__risk-comment:focus:not(:disabled) {
  border-color: #0033a0;
  box-shadow: 0 0 0 3px rgba(0, 51, 160, 0.08);
}
@media (max-width: 1200px) {
  .lp-card__grid--even-6 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .lp-card__grid--even-5 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 900px) {
  .lp-card__general {
    flex-wrap: wrap;
  }
  .lp-card__grid,
  .lp-card__grid--even-6,
  .lp-card__grid--even-5 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .lp-card__risk-row {
    grid-template-columns: minmax(120px, 1fr) 120px;
  }
  .lp-card__risk-comment {
    grid-column: 1 / -1;
  }
  .lp-card__risk-dd {
    width: 100%;
    justify-self: stretch;
  }
}
@media (max-width: 560px) {
  .lp-card__grid,
  .lp-card__grid--even-6,
  .lp-card__grid--even-5 {
    grid-template-columns: 1fr;
  }
}
.lp-card__trial-pair {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.lp-card__trial-pair :deep(.datepicker-wrap) {
  width: auto;
  flex: 1 1 9.5rem;
  min-width: 9.5rem;
  max-width: 12rem;
}
.lp-card__trial-pair .form-select {
  width: auto;
  min-width: 5.5rem;
  flex: 0 0 auto;
}
.lp-card__access {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  cursor: pointer;
}
.lp-card__access--readonly {
  cursor: default;
}
.lp-card__access-caption {
  font-size: 12px;
  font-weight: 500;
  color: #071b3a;
  line-height: 1.2;
}
.lp-card__access-check {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #0033a0;
  cursor: pointer;
}
.lp-card__access-check:disabled {
  cursor: default;
  opacity: 1;
}
.lp-card__indication-link {
  display: inline;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0033a0;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.lp-card__indication-link:hover {
  color: #001f63;
}
.lp-card__table--indications th:first-child,
.lp-card__table--indications td:first-child {
  width: 26%;
  vertical-align: top;
}
.lp-card__table--indications td:last-child {
  vertical-align: top;
}
.lp-card__wording-block {
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.45;
  color: #071b3a;
  background: #f7faff;
  border: 1px solid rgba(0, 51, 160, 0.12);
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}
.lp-indication-modal {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.lp-indication-modal__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  margin: 0;
  background: rgba(7, 27, 58, 0.45);
  backdrop-filter: blur(2px);
  cursor: pointer;
}
.lp-indication-modal__dialog {
  position: relative;
  z-index: 1;
  width: min(720px, 100%);
  max-height: min(80vh, 100%);
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(7, 27, 58, 0.22);
}
.lp-indication-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 51, 160, 0.1);
}
.lp-indication-modal__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #071b3a;
}
.lp-indication-modal__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(0, 51, 160, 0.18);
  background: #f5f7fb;
  color: #071b3a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.lp-indication-modal__body {
  padding: 20px;
}
</style>
